import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { BlogUiModel } from '../../../../../shared/ui-models/blog.ui-model';
import { buildPreviewDocument, PreviewLocale } from './blog-preview.helper';

export interface BlogPreviewModalProps {
  config: {
    showModal: boolean;
  };
  data: {
    blog: BlogUiModel;
    imageUrl?: string;
  };
  eventHandlers: {
    closeHandler: () => void;
  };
}

export const BlogPreviewModal: React.FC<BlogPreviewModalProps> = ({
  config,
  data,
  eventHandlers
}) => {
  const { t } = useTranslation();
  const [locale, setLocale] = useState<PreviewLocale>('en');

  const header = locale === 'ar' ? data.blog.headerAr : data.blog.header;
  const body = locale === 'ar' ? data.blog.bodyAr : data.blog.body;
  const isEmpty = !(header || body);

  const tabClassName = (localeKey: PreviewLocale) =>
    `cursor-pointer rounded-[4px] px-[1.5rem] py-[0.6rem] text-[1.4rem] font-bold ${
      locale === localeKey ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600'
    }`;

  return (
    <Modal
      config={{
        showModal: config.showModal,
        fullScreen: true,
        showClose: true,
        paperClassName: '!m-0 !h-screen !max-h-screen !w-screen !max-w-none !rounded-none',
        contentClassName: '!min-h-0 !p-0'
      }}
      eventHandlers={{ closeModalHandler: eventHandlers.closeHandler }}
    >
      <div className="flex h-full flex-col">
        <div className="flex min-h-[5rem] items-center justify-between gap-[1rem] pe-[6rem] ps-[2rem]">
          <div className="text-[2rem] font-bold text-primary-500">
            {t('BLOG_MODULE.EDIT_CONTAINER.PREVIEW_MODAL.TITLE')}
          </div>
          <div className="flex items-center gap-[0.5rem]">
            <div className={tabClassName('en')} onClick={() => setLocale('en')}>
              {t('BLOG_MODULE.EDIT_CONTAINER.TAB_EN')}
            </div>
            <div className={tabClassName('ar')} onClick={() => setLocale('ar')}>
              {t('BLOG_MODULE.EDIT_CONTAINER.TAB_AR')}
            </div>
          </div>
        </div>

        {isEmpty ? (
          <div className="flex flex-1 items-center justify-center bg-gray-50 text-[1.6rem] text-gray-500">
            {t('BLOG_MODULE.EDIT_CONTAINER.PREVIEW_MODAL.EMPTY')}
          </div>
        ) : (
          <iframe
            title={t('BLOG_MODULE.EDIT_CONTAINER.PREVIEW_MODAL.TITLE')}
            sandbox="allow-same-origin"
            srcDoc={buildPreviewDocument({
              header,
              body,
              imageUrl: data.imageUrl,
              locale,
              publishedAt: data.blog.publishedAt
            })}
            className="w-full flex-1 border-0 bg-[#faf8f6]"
          />
        )}
      </div>
    </Modal>
  );
};

export default BlogPreviewModal;
